import React, { useState, useRef, useCallback, useEffect } from "react";
import ScreenshotPreviewComponent, {
  ScreenshotPreview,
  ScreenshotSubmission,
} from "./Screenshot_Preview";

import ScreenRecordingManager from "../screen-recording/ScreenRecordingManager";
import { RecordingSubmission } from "../screen-recording/ScreenRecordingPreview";
import ToolBox from "../../ToolBox";
import ScreenShareInstruction from '../../ScreenShareInstruction';
import { markTourGuideDone, getTourGuideStatus, TOUR_GUIDE_KEYS } from '../../../../api/urls';
import SharedScreenStreamManager from "../SharedScreenStreamManager";
import CameraShutterEffect from "../../../../utils/CameraShutter";
import {
  compressImage,
  getImageSizeMB,
  needsCompression,
} from "../../../../utils/imageCompression";

export interface SelectionArea {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface ScreenshotManagerProps {
  onToolClick?: (toolId: string) => void;
  onScreenshotSubmit?: (submission: ScreenshotSubmission) => void;
  onRecordingSubmit?: (submission: RecordingSubmission) => void;
  onCaptureEnd?: () => void;
  uniqueFeedbackId?: string;
  isMuted?: boolean;
  cardId: number;
  hasUrl?: boolean;
  compact?: boolean;
  isFullscreen?: boolean;
}

const getIframeUrl = (): boolean => {
  const iframe = document.querySelector("iframe") as HTMLIFrameElement;
  if (!iframe) return false;

  try {
    if (!iframe.src || iframe.src === "about:blank" || iframe.src === "") {
      return false;
    }

    const iframeRect = iframe.getBoundingClientRect();
    if (iframeRect.width === 0 || iframeRect.height === 0) {
      return false;
    }

    return true;
  } catch (error) {
    console.log("Error checking iframe URL:", error);
    return false;
  }
};

const ScreenshotManager: React.FC<ScreenshotManagerProps> = ({
  onToolClick,
  onScreenshotSubmit,
  onRecordingSubmit,
  onCaptureEnd,
  uniqueFeedbackId,
  isMuted = false,
  cardId,
  hasUrl,
  compact = false,
  isFullscreen = false,
}) => {
  const [isToolHidden, setIsToolHidden] = useState(false);
  const [screenshotPreview, setScreenshotPreview] =
    useState<ScreenshotPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRecordingTools, setShowRecordingTools] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [showInstruction, setShowInstruction] = useState(false);
  const [pendingToolId, setPendingToolId] = useState<string | null>(null);
  const [apiCompletedGuides, setApiCompletedGuides] = useState<string[]>([]);

  const currentFeedbackIdRef = useRef<string | undefined>(uniqueFeedbackId);

  const sharedStreamManager = useRef(SharedScreenStreamManager.getInstance());
  const consumerIdRef = useRef(`screenshot-${Date.now()}`);

  useEffect(() => {
    console.log("ScreenshotManager - uniqueFeedbackId changed:", {
      previous: currentFeedbackIdRef.current,
      new: uniqueFeedbackId,
      type: typeof uniqueFeedbackId,
      isValid: uniqueFeedbackId && String(uniqueFeedbackId).trim() !== "",
    });

    currentFeedbackIdRef.current = uniqueFeedbackId;
  }, [uniqueFeedbackId]);

  // Fetch server-side tour status once on mount
  useEffect(() => {
    getTourGuideStatus().then(setApiCompletedGuides);
  }, []);

  interface Html2CanvasOptions {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    useCORS?: boolean;
    allowTaint?: boolean;
    scale?: number;
    logging?: boolean;
    ignoreElements?: (element: Element) => boolean;
    windowWidth?: number;
    windowHeight?: number;
    backgroundColor?: string;
    removeContainer?: boolean;
  }

  interface Html2Canvas {
    (
      element: HTMLElement,
      options?: Html2CanvasOptions
    ): Promise<HTMLCanvasElement>;
  }

  const loadHtml2Canvas = useCallback((): Promise<Html2Canvas> => {
    return new Promise((resolve, reject) => {
      if ((window as any).html2canvas) {
        resolve((window as any).html2canvas as Html2Canvas);
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      script.onload = () => resolve((window as any).html2canvas as Html2Canvas);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  // ✅ FIXED: Enhanced iframe dimensions detection with cross-origin check
  const getIframeDimensions = useCallback(() => {
    const iframe = document.querySelector("iframe") as HTMLIFrameElement;
    if (!iframe) {
      console.log("No iframe found");
      return null;
    }

    const iframeRect = iframe.getBoundingClientRect();

    if (iframeRect.width === 0 || iframeRect.height === 0) {
      console.error("❌ Iframe has zero dimensions");
      return null;
    }

    // ✅ NEW: Get the actual visible iframe area (exclude any clipping)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate the actual visible portion of the iframe
    const visibleLeft = Math.max(0, iframeRect.left);
    const visibleTop = Math.max(0, iframeRect.top);
    const visibleRight = Math.min(viewportWidth, iframeRect.right);
    const visibleBottom = Math.min(viewportHeight, iframeRect.bottom);

    const visibleWidth = visibleRight - visibleLeft;
    const visibleHeight = visibleBottom - visibleTop;

    // ✅ Use the visible dimensions for capture
    const actualLeft = iframeRect.left;
    const actualTop = iframeRect.top;
    const actualWidth = iframeRect.width;
    const actualHeight = iframeRect.height;

    // ✅ Detect cross-origin restrictions
    let canAccessIframe = false;
    let iframeSrc = iframe.src || "unknown";

    try {
      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        canAccessIframe = true;
        console.log("✅ Iframe is accessible (same-origin or CORS enabled)");
      }
    } catch (e) {
      console.log("⚠️ Iframe is cross-origin restricted:", iframeSrc);
    }

    console.log("📐 Iframe dimensions calculated:", {
      left: actualLeft,
      top: actualTop,
      width: actualWidth,
      height: actualHeight,
      visibleWidth,
      visibleHeight,
      screenWidth: viewportWidth,
      screenHeight: viewportHeight,
      canAccessIframe,
      iframeSrc,
    });

    return {
      left: actualLeft,
      top: actualTop,
      width: actualWidth,
      height: actualHeight,
      iframe: iframe,
      viewportLeft: visibleLeft,
      viewportTop: visibleTop,
      originalHeight: actualHeight,
      navigationBarHeight: 0,
      canAccessIframe,
      iframeSrc,
      // ✅ NEW: Add visible dimensions for proper cropping
      visibleWidth,
      visibleHeight,
    };
  }, []);

  // ✅ FIXED: Enhanced shared stream capture with better stability
 const captureIframeWithSharedStream = useCallback(
  async (iframeDimensions: any): Promise<string | null> => {
    const stream = sharedStreamManager.current.getCurrentStream();
    if (!stream || !stream.active) {
      console.log("No active shared stream available");
      return null;
    }

    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      video.autoplay = false;

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Video play failed:", error);
          resolve(null);
        });
      }

      video.addEventListener("loadedmetadata", () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try {
                const iframe = iframeDimensions.iframe;
                const iframeRect = iframe.getBoundingClientRect();

                if (video.readyState < 2) {
                  console.error("❌ Video not ready yet");
                  resolve(null);
                  return;
                }

                // ✅ Get all measurements
                const windowOuterHeight = window.outerHeight;
                const windowInnerHeight = window.innerHeight;
                const windowOuterWidth = window.outerWidth;
                const windowInnerWidth = window.innerWidth;
                const windowScreenX = window.screenX || window.screenLeft || 0;
                const windowScreenY = window.screenY || window.screenTop || 0;

                // ✅ CRITICAL: Detect if video captured screen or window
                const isMacOS = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
                const isFullscreen = window.innerHeight === window.screen.height && 
                                     window.innerWidth === window.screen.width;
                
                // Check if video dimensions match screen (not window)
                const videoMatchesScreen = Math.abs(video.videoWidth / video.videoHeight - 
                                                    window.screen.width / window.screen.height) < 0.1;
                const videoMatchesWindow = Math.abs(video.videoWidth / video.videoHeight - 
                                                     windowOuterWidth / windowOuterHeight) < 0.1;

                console.log("📊 Capture Analysis:", {
                  platform: isMacOS ? "macOS" : "Windows",
                  isFullscreen,
                  videoSize: { width: video.videoWidth, height: video.videoHeight },
                  screenSize: { width: window.screen.width, height: window.screen.height },
                  windowSize: { outer: windowOuterWidth, inner: windowInnerHeight },
                  windowPosition: { x: windowScreenX, y: windowScreenY },
                  videoMatchesScreen,
                  videoMatchesWindow,
                  videoAspect: (video.videoWidth / video.videoHeight).toFixed(3),
                  screenAspect: (window.screen.width / window.screen.height).toFixed(3),
                  windowAspect: (windowOuterWidth / windowOuterHeight).toFixed(3)
                });

                // ✅ Calculate chrome offset (same for all)
                const chromeTop = windowOuterHeight - windowInnerHeight;
                const chromeLeft = Math.max(0, (windowOuterWidth - windowInnerWidth) / 2);

                let scaleX;
                let scaleY;
                let videoIframeLeft;
                let videoIframeTop;

                // ✅ DECISION LOGIC: Choose calculation method based on what was captured
                if (isMacOS && videoMatchesScreen && !isFullscreen) {
                  // ✅ macOS NON-FULLSCREEN: Video captured entire screen
                  console.log("🍎 macOS Screen Capture Mode (non-fullscreen)");
                  
                  scaleX = video.videoWidth / window.screen.width;
                  scaleY = video.videoHeight / window.screen.height;
                  
                  // Calculate absolute position: window position + chrome + iframe position
                  const absoluteIframeLeft = windowScreenX + chromeLeft + iframeRect.left;
                  const absoluteIframeTop = windowScreenY + chromeTop + iframeRect.top;
                  
                  videoIframeLeft = Math.max(0,
                    Math.min(
                      absoluteIframeLeft * scaleX,
                      video.videoWidth - 10
                    )
                  );

                  videoIframeTop = Math.max(0,
                    Math.min(
                      absoluteIframeTop * scaleY,
                      video.videoHeight - 10
                    )
                  );

                  console.log("📐 Screen-based calculation:", {
                    absoluteIframeLeft,
                    absoluteIframeTop,
                    scaleX: scaleX.toFixed(4),
                    scaleY: scaleY.toFixed(4),
                    videoIframeLeft: videoIframeLeft.toFixed(2),
                    videoIframeTop: videoIframeTop.toFixed(2)
                  });
                  
                } else {
                  // ✅ ORIGINAL WORKING LOGIC: For Windows, macOS fullscreen, or window capture
                  console.log(isFullscreen 
                    ? "🍎 macOS Fullscreen Mode (original logic)" 
                    : "🪟 Window Capture Mode (original logic)");
                  
                  scaleX = video.videoWidth / windowOuterWidth;
                  scaleY = video.videoHeight / windowOuterHeight;
                  
                  videoIframeLeft = Math.max(0,
                    Math.min(
                      (iframeRect.left + chromeLeft) * scaleX,
                      video.videoWidth - 10
                    )
                  );

                  videoIframeTop = Math.max(0,
                    Math.min(
                      (iframeRect.top + chromeTop) * scaleY,
                      video.videoHeight - 10
                    )
                  );

                  console.log("📐 Window-based calculation:", {
                    chromeTop,
                    chromeLeft,
                    iframeRectTop: iframeRect.top,
                    iframeRectLeft: iframeRect.left,
                    scaleX: scaleX.toFixed(4),
                    scaleY: scaleY.toFixed(4),
                    videoIframeLeft: videoIframeLeft.toFixed(2),
                    videoIframeTop: videoIframeTop.toFixed(2)
                  });
                }

                const videoIframeWidth = Math.min(
                  iframeRect.width * scaleX,
                  video.videoWidth - videoIframeLeft
                );

                const videoIframeHeight = Math.min(
                  iframeRect.height * scaleY,
                  video.videoHeight - videoIframeTop
                );

                console.log("📐 Final Crop Region:", {
                  videoIframeLeft: videoIframeLeft.toFixed(2),
                  videoIframeTop: videoIframeTop.toFixed(2),
                  videoIframeWidth: videoIframeWidth.toFixed(2),
                  videoIframeHeight: videoIframeHeight.toFixed(2)
                });

                if (videoIframeWidth <= 0 || videoIframeHeight <= 0) {
                  console.error("❌ Invalid capture dimensions:", {
                    videoIframeWidth,
                    videoIframeHeight
                  });
                  resolve(null);
                  return;
                }

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d", {
                  alpha: false,
                  desynchronized: false,
                  willReadFrequently: false,
                  imageSmoothingEnabled: true,
                  imageSmoothingQuality: "high",
                }) as CanvasRenderingContext2D | null;

                if (!ctx) {
                  console.error("❌ Failed to get canvas context");
                  resolve(null);
                  return;
                }

                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                canvas.width = iframeRect.width * dpr;
                canvas.height = iframeRect.height * dpr;
                ctx.scale(dpr, dpr);

                console.log("🎨 Canvas dimensions:", {
                  canvasWidth: canvas.width,
                  canvasHeight: canvas.height,
                  displayWidth: iframeRect.width,
                  displayHeight: iframeRect.height,
                  dpr
                });

                try {
                  ctx.fillStyle = "#ffffff";
                  ctx.fillRect(0, 0, iframeRect.width, iframeRect.height);

                  ctx.drawImage(
                    video,
                    videoIframeLeft,
                    videoIframeTop,
                    videoIframeWidth,
                    videoIframeHeight,
                    0,
                    0,
                    iframeRect.width,
                    iframeRect.height
                  );

                  let dataUrl: string | null = null;

                  try {
                    dataUrl = canvas.toDataURL("image/png", 1.0);
                  } catch (pngError) {
                    console.warn("⚠️ PNG export failed, trying JPEG:", pngError);
                    try {
                      dataUrl = canvas.toDataURL("image/jpeg", 0.95);
                    } catch (jpegError) {
                      console.error("❌ Both PNG and JPEG failed:", jpegError);
                    }
                  }

                  if (!dataUrl || 
                      (!dataUrl.startsWith("data:image/png") && 
                       !dataUrl.startsWith("data:image/jpeg"))) {
                    console.error("❌ Invalid canvas output");
                    resolve(null);
                    return;
                  }

                  if (dataUrl.length < 1000) {
                    console.error("❌ Canvas output suspiciously small");
                    resolve(null);
                    return;
                  }

                  console.log("✅ Screenshot captured successfully");
                  resolve(dataUrl);
                } catch (drawError) {
                  console.error("❌ Canvas drawing failed:", drawError);
                  resolve(null);
                }
              } catch (error) {
                console.error("❌ Screenshot capture error:", error);
                resolve(null);
              }
            });
          });
        });
      });

      video.addEventListener("error", (e) => {
        console.error("❌ Video error:", e);
        resolve(null);
      });

      setTimeout(() => {
        console.error("❌ Screenshot capture timeout");
        resolve(null);
      }, 10000);
    });
  },
  []
);

  // ✅ FIXED: Screen API capture with same improvements
  const captureIframeWithScreenAPI = useCallback(
  async (iframeDimensions: any): Promise<string | null> => {
    try {
      console.log("Requesting shared stream for screenshot...");

      const stream = await sharedStreamManager.current.getSharedStream({
        preferredWidth: 1920,
        preferredHeight: 1080,
        frameRate: 1,
        includeAudio: false,
      });

      if (!stream) {
        console.log("Failed to get shared stream");
        return null;
      }

      await new Promise((resolve) => setTimeout(resolve, 800));

      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;

        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.error("Video play failed:", error);
            resolve(null);
          });
        }

        video.addEventListener("loadedmetadata", () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try {
                const iframe = iframeDimensions.iframe;
                const iframeRect = iframe.getBoundingClientRect();

                const windowOuterHeight = window.outerHeight;
                const windowInnerHeight = window.innerHeight;
                const windowOuterWidth = window.outerWidth;
                const windowInnerWidth = window.innerWidth;
                const windowScreenX = window.screenX || window.screenLeft || 0;
                const windowScreenY = window.screenY || window.screenTop || 0;

                const isMacOS = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
                const isFullscreen = window.innerHeight === window.screen.height && 
                                     window.innerWidth === window.screen.width;
                
                const videoMatchesScreen = Math.abs(video.videoWidth / video.videoHeight - 
                                                    window.screen.width / window.screen.height) < 0.1;

                const chromeTop = windowOuterHeight - windowInnerHeight;
                const chromeLeft = Math.max(0, (windowOuterWidth - windowInnerWidth) / 2);

                let scaleX;
                let scaleY;
                let videoIframeLeft;
                let videoIframeTop;

                if (isMacOS && videoMatchesScreen && !isFullscreen) {
                  // macOS screen capture mode
                  scaleX = video.videoWidth / window.screen.width;
                  scaleY = video.videoHeight / window.screen.height;
                  
                  const absoluteIframeLeft = windowScreenX + chromeLeft + iframeRect.left;
                  const absoluteIframeTop = windowScreenY + chromeTop + iframeRect.top;
                  
                  videoIframeLeft = Math.max(0,
                    Math.min(
                      absoluteIframeLeft * scaleX,
                      video.videoWidth - 10
                    )
                  );

                  videoIframeTop = Math.max(0,
                    Math.min(
                      absoluteIframeTop * scaleY,
                      video.videoHeight - 10
                    )
                  );
                } else {
                  // Original window capture mode
                  scaleX = video.videoWidth / windowOuterWidth;
                  scaleY = video.videoHeight / windowOuterHeight;
                  
                  videoIframeLeft = Math.max(0,
                    Math.min(
                      (iframeRect.left + chromeLeft) * scaleX,
                      video.videoWidth - 10
                    )
                  );

                  videoIframeTop = Math.max(0,
                    Math.min(
                      (iframeRect.top + chromeTop) * scaleY,
                      video.videoHeight - 10
                    )
                  );
                }

                const videoIframeWidth = Math.min(
                  iframeRect.width * scaleX,
                  video.videoWidth - videoIframeLeft
                );

                const videoIframeHeight = Math.min(
                  iframeRect.height * scaleY,
                  video.videoHeight - videoIframeTop
                );

                if (videoIframeWidth <= 0 || videoIframeHeight <= 0) {
                  console.error("❌ Invalid dimensions");
                  resolve(null);
                  return;
                }

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d", {
                  alpha: false,
                  desynchronized: false,
                  willReadFrequently: false,
                }) as CanvasRenderingContext2D | null;

                if (!ctx) {
                  resolve(null);
                  return;
                }

                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                canvas.width = iframeRect.width * dpr;
                canvas.height = iframeRect.height * dpr;
                ctx.scale(dpr, dpr);

                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, iframeRect.width, iframeRect.height);

                ctx.drawImage(
                  video,
                  videoIframeLeft,
                  videoIframeTop,
                  videoIframeWidth,
                  videoIframeHeight,
                  0,
                  0,
                  iframeRect.width,
                  iframeRect.height
                );

                const dataUrl = canvas.toDataURL("image/png", 1.0);

                if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
                  console.error("❌ Invalid output");
                  resolve(null);
                  return;
                }

                console.log("✅ Screen API screenshot captured");
                resolve(dataUrl);
              } catch (error) {
                console.error("❌ Capture error:", error);
                resolve(null);
              }
            });
          });
        });

        video.addEventListener("error", () => {
          console.error("❌ Video error");
          resolve(null);
        });

        setTimeout(() => {
          console.error("❌ Timeout");
          resolve(null);
        }, 10000);
      });
    } catch (error) {
      console.log("Screen Capture API failed:", error);
      return null;
    }
  },
  []
);
  // ✅ FIXED: Enhanced html2canvas fallback with multiple strategies
  const captureIframeWithHtml2Canvas = useCallback(
    async (iframeDimensions: any): Promise<string | null> => {
      try {
        console.log("📸 Using html2canvas fallback method");

        const html2canvas = await loadHtml2Canvas();
        const { iframe, width, height } = iframeDimensions;

        const screenWidth = window.innerWidth;
        let scale = 1;

        if (screenWidth >= 2560) scale = 2;
        else if (screenWidth >= 1920) scale = 1.5;
        else if (screenWidth >= 1440) scale = 1.2;

        let targetElement = iframe;
        let canvas: HTMLCanvasElement | null = null;

        // Strategy 1: Try to access iframe content directly
        try {
          const iframeDoc =
            iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc && iframeDoc.body) {
            console.log("✅ Strategy 1: Capturing iframe document body");

            canvas = await html2canvas(iframeDoc.body, {
              useCORS: true,
              allowTaint: true,
              scale: scale,
              logging: false,
              width: width,
              height: height,
              x: 0,
              y: 0,
              backgroundColor: "#ffffff",
              removeContainer: true,
              windowWidth: width,
              windowHeight: height,
            });
          }
        } catch (e) {
          console.log("Strategy 1 failed (CORS), trying Strategy 2...");
        }

        // Strategy 2: Capture iframe container
        if (!canvas) {
          console.log("✅ Strategy 2: Capturing iframe container");

          const wrapper = document.createElement("div");
          wrapper.style.position = "fixed";
          wrapper.style.left = `${iframe.getBoundingClientRect().left}px`;
          wrapper.style.top = `${iframe.getBoundingClientRect().top}px`;
          wrapper.style.width = `${width}px`;
          wrapper.style.height = `${height}px`;
          wrapper.style.zIndex = "9999";
          wrapper.style.backgroundColor = "#ffffff";

          document.body.appendChild(wrapper);

          try {
            canvas = await html2canvas(iframe, {
              useCORS: true,
              allowTaint: true,
              scale: scale,
              logging: false,
              backgroundColor: "#ffffff",
              removeContainer: true,
              width: width,
              height: height,
              x: 0,
              y: 0,
              ignoreElements: (element: Element) => {
                return (
                  element.classList.contains("screenshot-overlay") ||
                  element.classList.contains("screenshot-tool")
                );
              },
            });
          } finally {
            document.body.removeChild(wrapper);
          }
        }

        if (!canvas || canvas.width === 0 || canvas.height === 0) {
          console.error("❌ html2canvas produced empty canvas");
          return null;
        }

        // ✅ Export with multiple format fallbacks
        let dataUrl: string | null = null;

        try {
          dataUrl = canvas.toDataURL("image/png", 1.0);

          if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
            throw new Error("Invalid PNG output");
          }

          if (dataUrl.length < 1000) {
            throw new Error("PNG output too small");
          }

          console.log("✅ html2canvas PNG export successful");
          return dataUrl;
        } catch (pngError) {
          console.warn("⚠️ PNG export failed, trying JPEG:", pngError);

          try {
            dataUrl = canvas.toDataURL("image/jpeg", 0.95);

            if (!dataUrl || !dataUrl.startsWith("data:image/jpeg")) {
              throw new Error("Invalid JPEG output");
            }

            if (dataUrl.length < 1000) {
              throw new Error("JPEG output too small");
            }

            console.log("✅ html2canvas JPEG fallback successful");
            return dataUrl;
          } catch (jpegError) {
            console.error("❌ Both PNG and JPEG failed:", jpegError);
            return null;
          }
        }
      } catch (error) {
        console.error("❌ html2canvas failed:", error);
        return null;
      }
    },
    [loadHtml2Canvas]
  );

  // ✅ FIXED: Enhanced main capture function with better error messages
  const captureFullWebsiteScreenshot = useCallback(async () => {
    setIsLoading(true);

    const hideForCapture = () => {
      document.querySelectorAll('[data-feebdack-ui-overlay]').forEach(el => {
        (el as HTMLElement).style.opacity = '0';
        (el as HTMLElement).style.pointerEvents = 'none';
      });
    };
    const restoreAfterCapture = () => {
      document.querySelectorAll('[data-feebdack-ui-overlay]').forEach(el => {
        (el as HTMLElement).style.opacity = '';
        (el as HTMLElement).style.pointerEvents = '';
      });
    };

    const currentFeedbackId = currentFeedbackIdRef.current;

    try {
      const iframeDimensions = getIframeDimensions();

      if (!iframeDimensions) {
        alert("No website is currently loaded to capture.");
        setIsLoading(false);
        setIsToolHidden(false);
        return;
      }

      const { width, height, canAccessIframe, iframeSrc } = iframeDimensions;
      let dataUrl: string | null = null;
      let userCancelledPermission = false;
      let captureMethod: string = "unknown";

      console.log("📸 Attempting to capture:", {
        iframeSrc,
        canAccessIframe,
        dimensions: { width, height },
      });

      // Strategy 1: Existing shared stream
      if (sharedStreamManager.current.isStreamActive()) {
        console.log("Using existing shared stream");
        captureMethod = "shared-stream";

        hideForCapture();
        // ✅ FIX: Play flash/sound FIRST, wait for completion, THEN capture
        await CameraShutterEffect.trigger(isMuted);

        // ✅ NEW: Wait for flash animation to complete (typically 200-300ms)
        await new Promise((resolve) => setTimeout(resolve, 400));

        dataUrl = await captureIframeWithSharedStream(iframeDimensions);

        if (dataUrl) {
          console.log("✅ Screenshot captured via shared stream");
        }
      }

      // Strategy 2: Request shared stream
      if (!dataUrl) {
        console.log("Requesting shared stream");
        captureMethod = "screen-api";

        try {
          const stream = await sharedStreamManager.current.getSharedStream({
            preferredWidth: 1920,
            preferredHeight: 1080,
            frameRate: 1,
            includeAudio: false,
          });

          if (!stream) {
            userCancelledPermission = true;
          } else {
            hideForCapture();
            // ✅ FIX: Play flash/sound FIRST, wait for completion, THEN capture
            await CameraShutterEffect.trigger(isMuted);

            // ✅ NEW: Wait for flash animation to complete
            await new Promise((resolve) => setTimeout(resolve, 400));

            dataUrl = await captureIframeWithScreenAPI(iframeDimensions);

            if (dataUrl) {
              console.log("✅ Screenshot captured via Screen API");
            }
          }
        } catch (error: any) {
          if (
            error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError" ||
            error.message?.includes("denied") ||
            error.message?.includes("cancelled")
          ) {
            userCancelledPermission = true;
          }
        }
      }

      if (userCancelledPermission) {
        console.log("🚫 Screenshot cancelled by user");
        setIsLoading(false);
        setIsToolHidden(false);
        return;
      }

      // Strategy 3: html2canvas fallback
      if (!dataUrl) {
        console.log("Falling back to html2canvas");
        captureMethod = "html2canvas";

        hideForCapture();
        // ✅ FIX: Play flash/sound FIRST, wait for completion, THEN capture
        await CameraShutterEffect.trigger(isMuted);

        // ✅ NEW: Wait for flash animation to complete
        await new Promise((resolve) => setTimeout(resolve, 400));

        dataUrl = await captureIframeWithHtml2Canvas(iframeDimensions);

        if (dataUrl) {
          console.log("✅ Screenshot captured via html2canvas");
        }
      }

      // Rest of the error handling and compression logic remains the same...
      if (!dataUrl) {
        console.error("❌ Failed to capture screenshot");

        let errorMessage = "Failed to capture screenshot. ";

        if (!canAccessIframe) {
          errorMessage += `This website (${
            new URL(iframeSrc).hostname
          }) may be blocking screenshots due to security restrictions. Try:\n\n`;
          errorMessage +=
            "1. Use the 'Share Screen' permission when prompted\n";
          errorMessage += "2. Select 'Entire Screen' or 'Application Window'\n";
          errorMessage +=
            "3. Try a different browser (Chrome/Edge recommended)";
        } else {
          errorMessage += "Please try again. If the problem persists:\n\n";
          errorMessage += "1. Refresh the page and reload the website\n";
          errorMessage += "2. Grant screen sharing permission when prompted\n";
          errorMessage += "3. Check browser console for details";
        }

        alert(errorMessage);
        setIsLoading(false);
        setIsToolHidden(false);
        return;
      }

      console.log("✅ Screenshot captured successfully:", {
        method: captureMethod,
        dataUrlLength: dataUrl.length,
        sizeInMB: getImageSizeMB(dataUrl).toFixed(2) + "MB",
        website: iframeSrc,
      });

      // Compression logic...
      if (needsCompression(dataUrl, 1.0)) {
        const originalSizeMB = getImageSizeMB(dataUrl);
        console.log(
          `🗜️ Large screenshot detected (${originalSizeMB.toFixed(
            2
          )}MB), compressing...`
        );

        try {
          dataUrl = await compressImage(dataUrl, {
            maxSizeMB: 0.9,
            initialQuality: 0.9,
            maxDimension: 1920,
            format: "auto",
          });

          console.log("✅ Screenshot compressed after capture:", {
            before: originalSizeMB.toFixed(2) + "MB",
            after: getImageSizeMB(dataUrl).toFixed(2) + "MB",
          });
        } catch (compressionError) {
          console.warn("⚠️ Post-capture compression failed:", compressionError);
        }
      }

      const mockArea: SelectionArea = {
        startX: iframeDimensions.viewportLeft,
        startY: iframeDimensions.viewportTop,
        endX: iframeDimensions.viewportLeft + width,
        endY: iframeDimensions.viewportTop + height,
      };

      const previewWithFeedbackId: ScreenshotPreview & {
        uniqueFeedbackId?: string;
      } = {
        dataUrl,
        area: mockArea,
        actualWidth: width,
        actualHeight: height,
        uniqueFeedbackId: currentFeedbackId,
      };

      setScreenshotPreview(previewWithFeedbackId);
    } catch (error: any) {
      console.error("Screenshot capture failed:", error);

      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setIsLoading(false);
        setIsToolHidden(false);
        return;
      }

      let errorMessage = "Screenshot capture failed: " + error.message;

      if (error.message?.includes("CORS")) {
        errorMessage =
          "This website blocks cross-origin screenshots. Please use 'Share Screen'.";
      } else if (error.message?.includes("timeout")) {
        errorMessage = "Screenshot timed out. Please try again.";
      }

      alert(errorMessage);
      setIsToolHidden(false);
    } finally {
      restoreAfterCapture();
      setIsLoading(false);
    }
  }, [
    getIframeDimensions,
    captureIframeWithSharedStream,
    captureIframeWithScreenAPI,
    captureIframeWithHtml2Canvas,
    isMuted,
  ]);

  const shouldShowInstruction = useCallback((projectId: number): boolean => {
    // Server-side: if guide already marked done, never show again
    if (apiCompletedGuides.includes(String(TOUR_GUIDE_KEYS.SCREEN_SHARE_INSTRUCTION))) {
      return false;
    }
    // Local fallback: per-project dismissal
    try {
      const dismissedProjects = JSON.parse(
        localStorage.getItem('screen_share_instruction_dismissed') || '[]'
      );
      return !dismissedProjects.includes(projectId);
    } catch (error) {
      console.error('Error checking instruction status:', error);
      return true;
    }
  }, [apiCompletedGuides]);


   const handleToolClick = useCallback(
    async (toolId: string) => {
      if (toolId === "screenshot") {
        const iframeDimensions = getIframeDimensions();
        if (!iframeDimensions) {
          alert("Please load a website first.");
          return;
        }

        // ✅ NEW: Check if we need to show instruction before taking screenshot
        const needsStream = !sharedStreamManager.current.isStreamActive();
        if (needsStream && shouldShowInstruction(cardId)) {
          setPendingToolId('screenshot');
          setShowInstruction(true);
          return;
        }

        setScreenshotPreview(null);
        await captureFullWebsiteScreenshot();
      }

      if (toolId === "record") {
        const iframeDimensions = getIframeDimensions();
        if (!iframeDimensions) {
          alert("Please load a website first.");
          return;
        }

        // ✅ NEW: Check if we need to show instruction before recording
        const needsStream = !sharedStreamManager.current.isStreamActive();
        if (needsStream && shouldShowInstruction(cardId)) {
          setPendingToolId('record');
          setShowInstruction(true);
          return;
        }

        setShowRecordingTools(true);
      }

      if (onToolClick) {
        onToolClick(toolId);
      }
    },
    [onToolClick, getIframeDimensions, captureFullWebsiteScreenshot, cardId, shouldShowInstruction]
  );

  
  const handleInstructionClose = useCallback(async () => {
    setShowInstruction(false);

    // Continue with the pending action
    if (pendingToolId === 'screenshot') {
      setScreenshotPreview(null);
      await captureFullWebsiteScreenshot();
    } else if (pendingToolId === 'record') {
      setShowRecordingTools(true);
    }

    setPendingToolId(null);
  }, [pendingToolId, captureFullWebsiteScreenshot]);

  const handleInstructionDontShowAgain = useCallback(() => {
    setShowInstruction(false);
    setPendingToolId(null);
    markTourGuideDone(TOUR_GUIDE_KEYS.SCREEN_SHARE_INSTRUCTION);
  }, []);

  const handleClosePreview = useCallback(() => {
    setScreenshotPreview(null);
    setIsToolHidden(false);
    onCaptureEnd?.();
  }, [onCaptureEnd]);

  const handleCloseRecording = useCallback(() => {
    setShowRecordingTools(false);
    setIsToolHidden(false);
    onCaptureEnd?.();
  }, [onCaptureEnd]);

  const handleRecordingToolClick = useCallback(
    (toolId: string) => {
      if (onToolClick) {
        onToolClick(toolId);
      }
    },
    [onToolClick]
  );

  const handleScreenshotSubmit = useCallback(
    (submission: ScreenshotSubmission) => {
      const effectiveFeedbackId =
        submission.uniqueFeedbackId || currentFeedbackIdRef.current;

      const finalSubmission: ScreenshotSubmission = {
        ...submission,
        uniqueFeedbackId: effectiveFeedbackId,
      };

      if (onScreenshotSubmit) {
        onScreenshotSubmit(finalSubmission);
      }
    },
    [onScreenshotSubmit]
  );

  const handleRecordingSubmit = useCallback(
    (submission: RecordingSubmission) => {
      const effectiveFeedbackId =
        submission.uniqueFeedbackId || currentFeedbackIdRef.current;
      const finalSubmission: RecordingSubmission = {
        ...submission,
        uniqueFeedbackId: effectiveFeedbackId,
      };

      if (onRecordingSubmit) {
        onRecordingSubmit(finalSubmission);
      }
    },
    [onRecordingSubmit]
  );

  const downloadScreenshot = useCallback(() => {
    if (!screenshotPreview) return;

    const screenInfo = `${screenshotPreview.actualWidth}x${screenshotPreview.actualHeight}`;
    const timestamp = Date.now();

    const link = document.createElement("a");
    link.href = screenshotPreview.dataUrl;
    link.download = `screenshot-${screenInfo}-${timestamp}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [screenshotPreview]);

  useEffect(() => {
    const consumerId = consumerIdRef.current;

    sharedStreamManager.current.registerConsumer({
      id: consumerId,
      type: "screenshot",
      onStreamReady: (stream) => {
        console.log("Screenshot manager: Stream ready");
      },
      onStreamEnded: () => {
        console.log("Screenshot manager: Stream ended");
      },
    });

    return () => {
      sharedStreamManager.current.unregisterConsumer(consumerId);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (screenshotPreview) {
          handleClosePreview();
        } else if (showRecordingTools) {
          handleCloseRecording();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    screenshotPreview,
    showRecordingTools,
    handleClosePreview,
    handleCloseRecording,
  ]);

  useEffect(() => {
  const handlePreviewOpened = () => {
    console.log('ScreenshotManager: Preview opened - disabling shortcuts');
    setIsPreviewOpen(true);
  };

  const handlePreviewClosed = () => {
    console.log('ScreenshotManager: Preview closed - enabling shortcuts');
    setIsPreviewOpen(false);
  };

  window.addEventListener('preview-opened', handlePreviewOpened);
  window.addEventListener('preview-closed', handlePreviewClosed);

  return () => {
    window.removeEventListener('preview-opened', handlePreviewOpened);
    window.removeEventListener('preview-closed', handlePreviewClosed);
  };
}, []);

  useEffect(() => {
  const handleGlobalScreenshot = async () => {
    // ✅ CRITICAL: Block if preview is open
    if (isPreviewOpen) {
      console.log('ScreenshotManager: Screenshot blocked - preview is open');
      return;
    }

    const iframeDimensions = getIframeDimensions();
    if (!iframeDimensions) return;

    setScreenshotPreview(null);
    await captureFullWebsiteScreenshot();
  };

  const handleGlobalRecording = () => {
    // ✅ CRITICAL: Block if preview is open
    if (isPreviewOpen) {
      console.log('ScreenshotManager: Recording blocked - preview is open');
      return;
    }

    const iframeDimensions = getIframeDimensions();
    if (!iframeDimensions) return;

    setShowRecordingTools(true);
  };

  window.addEventListener("trigger-screenshot", handleGlobalScreenshot);
  window.addEventListener("trigger-recording", handleGlobalRecording);

  return () => {
    window.removeEventListener("trigger-screenshot", handleGlobalScreenshot);
    window.removeEventListener("trigger-recording", handleGlobalRecording);
  };
}, [getIframeDimensions, captureFullWebsiteScreenshot, isPreviewOpen]); 

  const getEffectiveFeedbackId = useCallback(() => {
    return (
      (screenshotPreview as any)?.uniqueFeedbackId ||
      currentFeedbackIdRef.current ||
      uniqueFeedbackId
    );
  }, [screenshotPreview, uniqueFeedbackId]);

  
  return (
    <>
      <ScreenshotPreviewComponent
        preview={screenshotPreview}
        isLoading={isLoading}
        onClose={handleClosePreview}
        onDownload={downloadScreenshot}
        onSubmit={handleScreenshotSubmit}
        uniqueFeedbackId={getEffectiveFeedbackId()}
      />

      {showRecordingTools && (
        compact ? (
          <ScreenRecordingManager
            onToolClick={handleRecordingToolClick}
            onClose={handleCloseRecording}
            onRecordingSubmit={handleRecordingSubmit}
            uniqueFeedbackId={currentFeedbackIdRef.current}
            compact
            isFullscreen={isFullscreen}
          />
        ) : (
          <div className="fixed inset-0 pointer-events-none z-[9999]">
            <div className="pointer-events-auto">
              <ScreenRecordingManager
                onToolClick={handleRecordingToolClick}
                onClose={handleCloseRecording}
                onRecordingSubmit={handleRecordingSubmit}
                uniqueFeedbackId={currentFeedbackIdRef.current}
                isFullscreen={isFullscreen}
              />
            </div>
          </div>
        )
      )}

      {/* Screen Share Instruction Popup */}
      <ScreenShareInstruction
        isOpen={showInstruction}
        onClose={handleInstructionClose}
        onDontShowAgain={handleInstructionDontShowAgain}
        projectId={cardId}
      />

      {/* Hide ToolBox while recording in compact mode */}
      {(!compact || !showRecordingTools) && (
        compact ? (
          <div
            className="px-2 py-1.5 rounded-[8px] border border-[#D7D9E2] shadow-xl inline-flex justify-center"
            style={{ background: "#FFFEFECC", backdropFilter: "blur(4px)" }}
          >
            <ToolBox onToolClick={handleToolClick} hasUrl={hasUrl !== undefined ? hasUrl : getIframeUrl()} isFullscreen={isFullscreen} />
          </div>
        ) : (
          <ToolBox onToolClick={handleToolClick} hasUrl={hasUrl !== undefined ? hasUrl : getIframeUrl()} isFullscreen={isFullscreen} />
        )
      )}
    </>
  );
};

export default ScreenshotManager;
